import React from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import { Step, StepConnector, StepIconProps, StepLabel, Stepper } from '@mui/material'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { useAppSelector } from '@/redux/store'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import getVisibleStepIndex, { VISIBLE_STEPS } from '../../_helpers/getVisibleStep'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    padding: 0,
    background: 'none',
  },
  // Poppins 16 - MUI sets its own typeface on the label, so the brand one is set here directly
  label: {
    fontFamily: `${theme.brandFontFamilies.body} !important`,
    fontSize: theme.brandTypography.text.regular.fontSize,
    color: `${theme.palette.brand.text.secondary} !important`,
    whiteSpace: 'nowrap',
    fontWeight: `${theme.brandTypography.weights.medium} !important`,
  },
  activeLabel: {
    color: `${theme.palette.brand.text.primary} !important`,
    fontWeight: `${theme.brandTypography.weights.semibold} !important`,
  },
  connector: {
    '& .MuiStepConnector-line': {
      borderTopWidth: 2,
      borderColor: theme.palette.brand.divider,
    },
    '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
      borderColor: theme.palette.brand.main,
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: theme.borderRadiusPill,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.brand.text.secondary,
    fontSize: theme.brandTypography.text.regular.fontSize,
    fontWeight: theme.brandTypography.weights.semibold,
  },
  iconActive: {
    borderColor: theme.palette.brand.main,
    background: theme.palette.brand.main,
    color: theme.palette.primary.contrastText,
  },
  iconCompleted: {
    borderColor: theme.palette.brand.main,
    background: theme.palette.brand.main,
  },
  hideInactiveLabels: {
    [theme.breakpoints.down(768)]: {
      '& .MuiStepLabel-label:not(.Mui-active)': {
        display: 'none',
      },
    },
  },
}))

const UpgradeStepIcon = ({ active, completed, icon }: StepIconProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  return (
    <div className={clsx(classes.icon, active && classes.iconActive, completed && classes.iconCompleted)}>
      {completed
        ? <CheckRoundedIcon style={{ color: theme.palette.primary.contrastText }} />
        : icon}
    </div>
  )
}

interface UpgradeStepperProps {
  className?: string
}

const UpgradeStepper = ({ className }: UpgradeStepperProps) => {
  const classes = useStyles()
  const { activeStep, orderCompleted } = useAppSelector(state => state.upgrade.wizard)
  const activeIndex = orderCompleted ? VISIBLE_STEPS.length : getVisibleStepIndex(activeStep)

  return (
    <Stepper
      activeStep={activeIndex}
      className={clsx(classes.root, classes.hideInactiveLabels, className)}
      connector={<StepConnector className={classes.connector} />}
      data-qs-id='checkout-stepper'>
      {VISIBLE_STEPS.map((step, index) => (
        <Step key={step} completed={index < activeIndex}>
          <StepLabel
            slots={{ stepIcon: UpgradeStepIcon }}
            classes={{ label: classes.label, active: classes.activeLabel }}>
            {lang.upgrade.wizard.stepper.titles[step]}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  )
}

export default UpgradeStepper
